/**
 * istanbul ignore file
 *
 * @format
 */

/** @format */

import {
	ATCEventChildSku,
	ATCEventSkuData,
	BundleData,
	Chain,
	HeaderStoreSelection,
	IScoreCardSkuResponse,
	PersonalizationData,
	ProcessedProduct,
	ProductSku,
	SkuDefiningAttribute,
	StyleDefiningAttribute
} from '@shared/data-access/interfaces';
import {
	AnalyticsService,
	BreakpointsService,
	FileService,
	Scene7Service,
	Store,
	UtilityService,
	WINDOW
} from '@shared/data-access/services';
import {
	AsyncPipe,
	CurrencyPipe,
	DatePipe,
	NgTemplateOutlet,
	formatCurrency
} from '@angular/common';
import {
	Component,
	ComponentRef,
	DestroyRef,
	OnDestroy,
	OnInit,
	TemplateRef,
	ViewContainerRef,
	ViewRef,
	computed,
	effect,
	inject,
	input,
	signal,
	untracked,
	viewChild
} from '@angular/core';
import {
	DisplayName,
	PlacementID,
	PriceRerankLevel,
	ProductRecommendationsPageType
} from '@product-recommendations/data-access';
import {
	DynamicDrawerSizes,
	ProductSelectionUtilityService,
	getSelectedWarranty
} from '@pdp/utils';
import {
	HmfAlertBannerComponent,
	HmfButtonComponent,
	HmfDrawerComponent,
	HmfModalFactoryService
} from '@dsg-tech/homefield-angular';
import {
	HomefieldIconLibraryModule,
	HomefieldIconsLibraryService,
	homefieldIconAlertErrorOutlined,
	homefieldIconSignCircledCheck
} from '@dsg-tech/homefield-angular-icons';
import { Subject, Subscription, zip } from 'rxjs';
import { WarrantyPlanCtaWrapperComponent, WarrantyPlanSelectorComponent } from '@pdp/components';
import {
	atcDrawerWarrantyAddPlanClicked,
	atcSuccessDrawerClosed,
	atcSuccessDrawerOpened,
	selectAtcFulfillmentOption,
	selectAtcSuccess,
	selectAtcUserInputQty,
	selectIsMultipleProducts,
	selectWarrantyAddToExistingCartError
} from '@pdp/add-to-cart/data-access';
import { filter, take } from 'rxjs/operators';
import {
	selectBundleData,
	selectCustomProductPrice,
	selectFBTProducts,
	selectKillswitch,
	selectPreSaleInfo,
	selectPrimaryCategory,
	selectProductPartNumber,
	selectResolvedSku,
	selectSelectedBundlePruductsPrices,
	selectSelectedBundleSkus,
	selectSelectedPersonalizationOptions,
	selectSelectedWarrantyDetails,
	selectShouldShowWarrantySelectorOnAtc,
	selectWarrantiesForResolvedSku,
	selectWarrantyAnalyticsEcodes,
	selectWarrantyCallState,
	selectcustomProductData,
	selectWarrantiesEnabled
} from '@pdp/data-access/state';

import { BreakPointNew } from '@shared/data-access/enums';
import { Store as NGRXStore } from '@ngrx/store';
import { ProductRecommendationsComponent } from '@shared/components';
import { PurchaseWarrantyComponent } from '@dsg-tech/non-merch-common-externals';
import { ScorecardPointsComponent } from '@scorecard/components';
import { SelectedBundleSkus } from '@pdp/data-access/interfaces';
import { pdpJsonFileName } from '@pdp/data-access/constants';
import { selectUserStoreInfo } from '@state/data-access';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

interface ATCSuccessPersonalizationAttribute {
	name: string;
	value: PersonalizationData[keyof PersonalizationData];
}

@Component({
	selector: 'pdp-add-to-cart-success',
	templateUrl: './add-to-cart-success.component.html',
	styleUrl: './add-to-cart-success.component.scss',
	imports: [
		NgTemplateOutlet,
		HomefieldIconLibraryModule,
		PurchaseWarrantyComponent,
		HmfButtonComponent,
		ScorecardPointsComponent,
		AsyncPipe,
		CurrencyPipe,
		DatePipe,
		WarrantyPlanSelectorComponent,
		WarrantyPlanCtaWrapperComponent,
		ProductRecommendationsComponent,
		HmfAlertBannerComponent
	]
})
export class AddToCartSuccessComponent implements OnInit, OnDestroy {
	private addToCartDrawerRef: ComponentRef<HmfDrawerComponent>;

	readonly addToCartSuccessDrawerContent = viewChild.required<TemplateRef<unknown>>(
		'addToCartSuccessDrawerContent'
	);
	readonly initialContainer = viewChild.required('initialContainer', { read: ViewContainerRef });
	readonly targetContainer = viewChild('targetContainer', { read: ViewContainerRef });

	drawerRefToInsert: ViewRef | undefined;

	/**
	 * @description
	 * The product currently being displayed
	 */
	readonly product = input<ProcessedProduct>();
	readonly newDataScienceRecommendationsEnabledNoPer = input(false);
	readonly preOrderEnabled = input<boolean>();
	readonly isBundle = input(false);

	readonly window = inject(WINDOW) as Window;
	private readonly store = inject(Store);
	private readonly ngrxStore = inject(NGRXStore);
	private readonly chain = inject(Chain);
	private readonly utilityService = inject(UtilityService);
	private readonly pdslUtilityService = inject(ProductSelectionUtilityService);
	private readonly fileService = inject(FileService);
	private readonly scene7Service = inject(Scene7Service);
	private readonly analyticsService = inject(AnalyticsService);
	private readonly destroyRef = inject(DestroyRef);
	private readonly modalFactoryService = inject(HmfModalFactoryService);
	private readonly breakpointsService = inject(BreakpointsService);
	private readonly homefieldIconsLibraryService = inject(HomefieldIconsLibraryService);

	/**
	 * @description
	 * The cart URL
	 */
	public cartURL = `/OrderItemDisplay${this.chain.cartUrlParameters}`;

	/**
	 * @description
	 * The checkout URL
	 */
	public checkoutURL = `/${this.chain.checkoutUrlBase}${this.chain.checkoutUrlParameters}`;
	public checkoutBtnURL = `/${this.chain.checkoutUrlBase}`;

	/**
	 * @description
	 * Chain identifier
	 */
	public chainIdentifierAbbr = this.chain.chainIdentifierAbbr;

	public atcSuccess: boolean | null;

	public skuData: BundleData | ProductSku;

	/**
	 * @description
	 * Personalization values for personalied balls
	 */
	public personalization: ATCSuccessPersonalizationAttribute[] = [];

	/**
	 * @description
	 * Variable holds the boolean of whether the product added to cart is a shoe based on its primary category
	 */
	public productHasShoeCategory: boolean;

	public preOrderDate: number;

	public displayPreOrderText = false;

	atcSuccessSubscription$: Subscription;

	skuDataSubscription$: Subscription;

	public userInputQuantity = this.ngrxStore.selectSignal(selectAtcUserInputQty);

	public selectedFullfillmentOption$ = this.ngrxStore.select(selectAtcFulfillmentOption);

	public selectcustomProductData$ = this.ngrxStore.select(selectcustomProductData);

	public userSelectedStore$: Subscription;

	selectCustomProductPrice$ = this.ngrxStore.select(selectCustomProductPrice);

	warrantiesEnabled = this.ngrxStore.selectSignal(selectWarrantiesEnabled);
	selectedWarrantyDetails = this.ngrxStore.selectSignal(selectSelectedWarrantyDetails);
	selectedPlanSku = signal(null);
	shouldShowWarrantySelectorOnAtc = this.ngrxStore.selectSignal(
		selectShouldShowWarrantySelectorOnAtc
	);
	warrantyCallState = this.ngrxStore.selectSignal(selectWarrantyCallState);
	availableWarranties = this.ngrxStore.selectSignal(selectWarrantiesForResolvedSku);
	productEcode = this.ngrxStore.selectSignal(selectProductPartNumber);
	warrantyAnalyticsEcodes = this.ngrxStore.selectSignal(selectWarrantyAnalyticsEcodes);
	warrantyAddToExistingCartError = this.ngrxStore.selectSignal(
		selectWarrantyAddToExistingCartError
	);
	readonly purchaseWarranty = computed(() => {
		if (!this.warrantiesEnabled() || !this.selectedWarrantyDetails()) {
			return null;
		}
		return {
			planDisplayName: 'No Sweat Protection Plan',
			sellingPrice: this.selectedWarrantyDetails().sellingPrice
				? formatCurrency(this.selectedWarrantyDetails().sellingPrice, 'en-US', '$')
				: '',
			warrantyPeriod:
				this.selectedWarrantyDetails().athleteDescription?.ecommPlanDisplayName ?? '',
			warrantyQuantity: this.userInputQuantity() ?? 0
		};
	});
	shouldShowAddWarrantyPlanButton = computed(
		() =>
			this.shouldShowWarrantySelectorOnAtc() &&
			!this.purchaseWarranty() &&
			this.selectedPlanSku()
	);
	isDrawerOpen = signal(false);

	imageURL: string;

	storeDetails: HeaderStoreSelection;
	customImage: string;

	shortenPurchasePathUpdatesATCEnabled = this.ngrxStore.selectSignal(
		selectKillswitch('shortenPurchasePathUpdatesATCEnabled')
	);
	shortenPurchasePathCheckoutButtonATCEnabled = this.ngrxStore.selectSignal(
		selectKillswitch('shortenPurchasePathCheckoutButtonATCEnabled')
	);
	isLoadingStateRecsEnabled = this.ngrxStore.selectSignal(
		selectKillswitch('isLoadingStateRecsEnabled')
	);
	isRecsDisplayedInATCPanelEnabled = this.ngrxStore.selectSignal(
		selectKillswitch('isRecsDisplayedInATCPanelEnabled')
	);
	atcModalClassName = computed(() =>
		this.isRecsDisplayedInATCPanelEnabled() ? 'pdp-add-to-cart-success-modal' : ''
	);

	/**
	 * @description
	 * Go To Cart/Bag on ATC Panel.
	 */
	primaryButtonText: string;

	/**
	 * @description
	 * Success Message text on ATC Panel. Calia uses 'bag' instead of 'cart'
	 */
	atcSuccessText: string;
	bundleProductsSubscription$: Subscription;
	bundleProducts: SelectedBundleSkus[];
	bundlePrice$ = this.ngrxStore.select(selectSelectedBundlePruductsPrices);
	selectFBTProducts$ = this.ngrxStore.select(selectFBTProducts);
	selectIsMultipleProducts$ = this.ngrxStore.select(selectIsMultipleProducts);
	selectSelectedPersonalizationOptions$ = this.ngrxStore.select(
		selectSelectedPersonalizationOptions
	);

	pageType = ProductRecommendationsPageType;
	priceRerankLevel = PriceRerankLevel;
	placementID = PlacementID;
	displayName = DisplayName;
	drawerDirection: 'right' | 'bottom';

	private ngUnsubscribe: Subject<void> = new Subject();
	scoreCardData: IScoreCardSkuResponse[];

	constructor() {
		this.homefieldIconsLibraryService.registerIcons([
			homefieldIconAlertErrorOutlined,
			homefieldIconSignCircledCheck
		]);

		// When we update to Angular v20, we should update the modal service to make use of input binding instead
		// so we can just do that when we create the drawer instead of having to manually setInput in an effect
		// https://github.com/angular/angular/pull/60137
		effect(() => {
			const breakpoint = this.breakpointsService.breakpoint();
			this.drawerDirection = breakpoint <= BreakPointNew.SMALL ? 'bottom' : 'right';
			if (this.addToCartDrawerRef) {
				this.addToCartDrawerRef.setInput(
					'size',
					this.pdslUtilityService.getValueByScreenSize(DynamicDrawerSizes, breakpoint)
				);
				this.addToCartDrawerRef.setInput('direction', this.drawerDirection);
			}
		});

		effect(() => {
			if (
				this.isDrawerOpen() &&
				this.warrantyCallState() === 'success' &&
				untracked(this.availableWarranties).length > 0 &&
				untracked(this.shouldShowWarrantySelectorOnAtc)
			) {
				this.analyticsService.triggerAnalyticsEvent('WarrantyView', {
					ProductID: untracked(this.productEcode),
					WarrantyEcode: untracked(this.warrantyAnalyticsEcodes),
					WarrantyFindingMethod: 'ATC Modal'
				});
			}
		});
	}

	ngOnInit() {
		if (this.isBundle()) {
			this.skuDataSubscription$ = this.ngrxStore
				.select(selectBundleData)
				.subscribe((bundleData: BundleData) => {
					this.skuData = bundleData;
					this.imageURL = this.scene7Service.getProductImageUrl(
						this.skuData?.partNumber,
						'bundlebean',
						'',
						'$DSG_ATCIMG$'
					);
				});
		} else {
			this.skuDataSubscription$ = this.ngrxStore
				.select(selectResolvedSku)
				.subscribe((skuData: ProductSku) => {
					this.skuData = skuData;

					let colorString: string | null;

					if (skuData?.definingAttributes) {
						// replace any whitespace and / with _ in the color value
						const colorAttribute = skuData.definingAttributes.find(
							(definingAttribute) => definingAttribute.name === 'Color'
						);

						colorString = colorAttribute
							? colorAttribute.value.replace(/\s|\//g, '_')
							: null;
					}

					this.imageURL = this.scene7Service.getProductImageUrl(
						this.skuData?.parentPartNumber,
						'ProductBean',
						colorString,
						'$DSG_ATCIMG$'
					);
				});
		}

		this.atcSuccessSubscription$ = this.ngrxStore
			.select(selectAtcSuccess)
			.subscribe((atcSuccess) => {
				this.atcSuccess = atcSuccess;
				if (this.atcSuccess) {
					this.openAddToCartDrawer();
				}
				// See if product's primary category is a shoe category
				if (this.productHasShoeCategory === undefined && this.atcSuccess) {
					this.configureCategoryObservables();
				}
			});

		this.bundleProductsSubscription$ = this.ngrxStore
			.select(selectSelectedBundleSkus)
			.subscribe((products) => {
				this.bundleProducts = products;
			});

		this.selectcustomProductData$.subscribe((customData) => {
			this.customImage = customData?.imageURLs[0];
		});

		this.userSelectedStore$ = this.ngrxStore
			.select(selectUserStoreInfo)
			.subscribe((storeDetails) => {
				this.storeDetails = storeDetails;
			});

		this.processPersonalization();
		// scorecard data
		this.store
			.selectDistinctKey('SCORECARD_DATA')
			.pipe(
				filter((data) => {
					return data !== undefined;
				}),
				take(1)
			)
			.subscribe((data: IScoreCardSkuResponse[]) => {
				this.scoreCardData = data;
			});

		this.initializePreOrderData();
	}

	ngOnDestroy() {
		this.atcSuccessSubscription$?.unsubscribe();
		this.skuDataSubscription$?.unsubscribe();
		this.userSelectedStore$?.unsubscribe();

		this.ngUnsubscribe.next();
		this.ngUnsubscribe.complete();
	}

	public onDrawerClosed() {
		this.atcSuccess = null;
		this.ngrxStore.dispatch(atcSuccessDrawerClosed());
		this.isDrawerOpen.set(false);
	}

	public closeDrawer() {
		if (this.addToCartDrawerRef) {
			this.addToCartDrawerRef.instance.closeDrawer();
		}
	}

	public processPersonalization() {
		const personalizationKeyToDisplayText = {
			x_personalizationTextLine1: 'Line 1',
			x_personalizationTextLine2: 'Line 2',
			x_personalizationTextLine3: 'Line 3',
			x_personalizationLineStyle: 'Style',
			x_personalizationNumberLine1: 'Number'
		};

		this.selectSelectedPersonalizationOptions$.subscribe((personalizationData) => {
			this.personalization = [];
			if (personalizationData.personalization) {
				Object.keys(personalizationData.personalization).forEach((key) => {
					if (
						personalizationData.personalization[key] &&
						personalizationKeyToDisplayText[key]
					) {
						this.personalization.push({
							name: personalizationKeyToDisplayText[key],
							value: personalizationData.personalization[key]
						});
					}
				});
			}
		});
	}

	/**
	 * @description
	 * Gets info from category data file, finds the primary category and sets productCateoryNeedsClearanceMessaging
	 * Observables subscribed:
	 * CATEGORIE
	 * PRIMARY_CAT
	 */
	public configureCategoryObservables() {
		const CATEGORIES = this.fileService.getDataFileByType$(
			pdpJsonFileName.categoriesData,
			'json'
		);
		const primaryCategory$ = this.ngrxStore.select(selectPrimaryCategory);

		const DATA_OBSERVABLES = zip(CATEGORIES, primaryCategory$);

		DATA_OBSERVABLES.subscribe((values) => {
			// Ensure there is something to parse
			if (Array.isArray(values) && values.length > 0) {
				// Set the Category JSON file data
				const categoryJsonData = values[0];

				// Set the Primary Category data
				const primaryCategory = values[1];

				let categoryName: string;

				// Check to see if the Primary Category is defined
				if (primaryCategory) {
					categoryName =
						typeof primaryCategory.note !== 'undefined'
							? primaryCategory.note
							: primaryCategory.name;
				}

				// Check to see if the Category data and Primary Category are defined
				if (categoryJsonData && categoryName) {
					// Check if product category needs clearance messaging
					this.productHasShoeCategory = this.utilityService.isItemInList(
						categoryName,
						categoryJsonData.shoeCategories
					);
				}
			}
		});
	}

	public initializePreOrderData() {
		this.ngrxStore
			.select(selectPreSaleInfo)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((preSaleInfo) => {
				if (this.preOrderEnabled() && preSaleInfo.isPreSaleCurrent) {
					this.displayPreOrderText = true;
					this.preOrderDate = this.pdslUtilityService.formatDateToTimestamp(
						preSaleInfo.preSaleDate
					);
				} else {
					this.displayPreOrderText = false;
				}
			});
	}

	openAddToCartDrawer() {
		if (!this.addToCartDrawerRef) {
			this.addToCartDrawerRef = this.modalFactoryService.create(
				HmfDrawerComponent,
				{
					headerText: '',
					className: this.atcModalClassName(),
					closable: true,
					direction: this.drawerDirection,
					isCloseButtonSticky: true,
					size: this.pdslUtilityService.getValueByScreenSize(
						DynamicDrawerSizes,
						this.breakpointsService.breakpoint()
					)
				},
				this.addToCartSuccessDrawerContent()
			);

			this.addToCartDrawerRef.instance.closed
				.pipe(takeUntilDestroyed(this.destroyRef))
				.subscribe(() => {
					this.onDrawerClosed();
				});
		}

		// opened event tells us the drawer body is rendered, so we can safely place the body
		this.addToCartDrawerRef.instance?.opened.pipe(take(1)).subscribe(() => {
			this.moveDrawerBodyToDrawer();
		});
		this.addToCartDrawerRef.instance?.openDrawer();

		this.ngrxStore.dispatch(atcSuccessDrawerOpened());
		this.selectedPlanSku.set(null);
		this.isDrawerOpen.set(true);
	}

	moveDrawerBodyToDrawer() {
		if (!this.drawerRefToInsert) {
			this.drawerRefToInsert = this.initialContainer()?.detach();
		}
		this.targetContainer()?.insert(this.drawerRefToInsert);
	}

	trackByAttrName(index: number, item: StyleDefiningAttribute | SkuDefiningAttribute): string {
		return index.toString() + item.name;
	}

	trackByPers(index: number, item: ATCSuccessPersonalizationAttribute): string {
		return index.toString() + item.value;
	}

	trackByProdSku(index: number, item: SelectedBundleSkus): string {
		return index.toString() + item.sku?.name;
	}

	trackBySku(index: number, item: SkuDefiningAttribute): string {
		return index.toString() + item.name + item.value;
	}

	trackByFBTProduct(index: number, item: ATCEventSkuData | ATCEventChildSku): string {
		return index.toString() + item.title;
	}

	fireAnalyticsEvent(eventName: string) {
		this.analyticsService.triggerAnalyticsEvent('nonMerchLinkTracking', { name: eventName });
	}

	handleAddPlanClick() {
		const selectedWarranty = getSelectedWarranty(
			this.selectedPlanSku(),
			this.availableWarranties()
		);

		this.ngrxStore.dispatch(atcDrawerWarrantyAddPlanClicked({ selectedWarranty }));
	}
}
